import { Grid, Flex, Button, Loader } from '@mantine/core';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useEffect, useMemo, useState, Fragment } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { Input, SuccessModal, Phone, CustomModal } from '@jadwalh/ui';
import { useRoutesConst } from './../../../../../../../routes';
import axiosInstance from './../../../../../../../axios-instance';
import { IUser, updateUser } from './../../../../../../../api/users.api';
import { setErrors } from '@jadwalh/utils';
import { PopUpWrapper } from './styles';

import { getPermissions } from './../../../../../../../store/profile.slice';
import { toast } from 'react-toastify';

interface FormInitialValues {
  id: number;
  phone?: string;
  phone_number: string | number;
  phone_code: string;
  first_name: string;
  last_name: string;
  email: string;
}
const defaultValues: FormInitialValues = {
  id: 0,
  phone_number: '',
  phone_code: '',
  first_name: '',
  last_name: '',
  email: '',
};

export default function EditClient() {
  const queryClient = useQueryClient();
  const initialValues = useOutletContext<IUser>();
  const navigate = useNavigate();
  const { ROUTES } = useRoutesConst();
  const [search, setSearch] = useState(initialValues.phone_number);
  const [languagesData, setLanguagesData] = useState<
    { value: string; label: string }[]
  >([]);
  const { formatMessage } = useIntl();
  /* ------------------------- If Client Data Not Exist------------------------- */
  const [isEmptyData, setIsEmptyData] = useState(false);
  const [opened, setOpened] = useState(true); // State for the success modal
  const methods = useForm<FormInitialValues>({ defaultValues });
  const permissions = useSelector(getPermissions);

  const { handleSubmit, reset, setError } = methods;

  const updatedDefaultValues = useMemo(() => {
    return {
      ...defaultValues,
      id: initialValues?.id,
      first_name: initialValues?.first_name,
      last_name: initialValues?.last_name,
      email: initialValues?.email,
      phone_number: initialValues?.phone_number,
      phone_code: initialValues?.phone_code?.toString(),
    };
  }, [defaultValues]);

  useEffect(() => {
    // Reset the form with the updated default values
    reset(updatedDefaultValues);
  }, []);

  const { refetch } = useQuery({
    queryKey: ['userData', search],
    queryFn: () => axiosInstance.get(`app/users?search=00${search}`),
    select: (res) => res.data.data,
    onSuccess: (data) => {
      if (data && data.length > 0) {
        const { last_name, first_name, email, phone_code } = data[0];
        // Update the defaultValues object with the retrieved data
        const updatedDefaultValues = {
          ...defaultValues,
          first_name: first_name,
          last_name: last_name,
          email: email,
          phone_number: search,
          phone_code: phone_code,
        };

        // Reset the form with the updated default values
        reset(updatedDefaultValues);

        setIsEmptyData(false);
      } else {
        setIsEmptyData(true);
        reset({ ...defaultValues, phone_number: search }); // Reset the form to its original default values
      }
    },
    enabled: false,
  });

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: (data: FormInitialValues) => {
      return updateUser(initialValues.id, data);
    },
    onSuccess: (newData) => {
      queryClient.setQueriesData(
        [initialValues.id.toString(), 'clients'],
        newData.data
      );
      setOpened(false);
      toast.success(formatMessage({ id: 'newAppointmentAdded' }));
      setTimeout(() => {
        navigate(ROUTES.clientDetail.to({ id: initialValues.id }));
      }, 2000);
    },
    onError: (error: any) => {
      const errors = error.response.data.errors;
      setErrors(errors, setError);
    },
  });

  const onSubmit: SubmitHandler<FormInitialValues> = async (
    data: FormInitialValues
  ) => {
    data.phone_number = Number(data.phone_number);
    await mutateAsync(data);
  };

  if (Array.isArray(permissions) && !permissions.includes('users.update')) {
    return (
      <Flex justify="center">
        <Loader color="#303030"  />
      </Flex>
    );
  }

  return (
    <>
      <CustomModal
        opened={opened}
        onClose={() => {
          return;
        }}
        withCloseButton={false}
        size={'lg'}
        closeOnEscape={false}
        header={formatMessage({ id: 'editCustomerData' })}
      >
        <Fragment>
          <Grid>
            <Grid.Col xs={12}>
              <PopUpWrapper>
                <FormProvider {...methods}>
                  <form onSubmit={handleSubmit(onSubmit)} id="add-appointment">
                    <div className="popup-body">
                      <Grid h={'100%'}>
                        <Grid.Col xs={12} md={6}>
                          <Input
                            label={formatMessage({ id: 'first_name' })}
                            name="first_name"
                            registerOptions={{
                              required: {
                                value: true,
                                message: `${formatMessage({
                                  id: 'requiredFieldError',
                                })}`,
                              },
                            }}
                          />
                        </Grid.Col>
                        <Grid.Col xs={12} md={6}>
                          <Input
                            label={formatMessage({ id: 'last_name' })}
                            name="last_name"
                            registerOptions={{
                              required: {
                                value: true,
                                message: `${formatMessage({
                                  id: 'requiredFieldError',
                                })}`,
                              },
                            }}
                          />
                        </Grid.Col>

                        <Grid.Col xs={12}>
                          <Phone
                            moduleName="edit-client"
                            isAdding={false}
                            label={formatMessage({ id: 'phoneNumber' })}
                            codeRequired={false}
                            showPlaceholder={false}
                          />
                        </Grid.Col>
                        <Grid.Col xs={12}>
                          <Input
                            label={formatMessage({ id: 'Email' })}
                            name="email"
                            registerOptions={{
                              required: {
                                value: true,
                                message: `${formatMessage({
                                  id: 'requiredFieldError',
                                })}`,
                              },
                            }}
                          />
                        </Grid.Col>
                      </Grid>
                    </div>
                  </form>
                </FormProvider>
                <div className="popup-footer">
                  <div className="action_container">
                    <Flex align="center" gap={8}>
                      <Button
                        form="add-appointment"
                        loading={isLoading}
                        type="submit"
                      >
                        {!isLoading && formatMessage({ id: 'save' })}
                      </Button>
                      <Button
                        type="button"
                        className="cancelBtn"
                        onClick={() =>
                          navigate(
                            ROUTES.clientDetail.to({ id: initialValues.id })
                          )
                        }
                      >
                        {formatMessage({ id: 'cancellation' })}
                      </Button>
                    </Flex>
                  </div>
                </div>
              </PopUpWrapper>
            </Grid.Col>
          </Grid>
        </Fragment>
      </CustomModal>
    </>
  );
}
